// src/components/layout/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  InputBase,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  Switch,
  Badge,
  Tooltip,
  Fab,
  Zoom,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  School as SchoolIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  LibraryBooks as CoursesIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Assignment as AssignmentIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  Grade as GradeIcon,
  VideoCall as VideoCallIcon,           // ← NUEVO
  FiberManualRecord,                     // ← NUEVO
  KeyboardArrowUp,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AppContext';
import { useAmbito } from '../../context/AmbitoContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { sesionOnlineService, meService, chatService } from '../../services/api';

function Header() {
  const theme = useTheme();
  const { isDarkMode, toggleTheme, productoInfo } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, isFenagroSuperAdmin, isSuperAdminRedeprog } = useAuth();
  const { opciones: opcionesAmbito, ambitoId: ambitoActivoId, setAmbitoId: cambiarAmbito } = useAmbito();
  const [liveSessions, setLiveSessions] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  // ============ ALIANZAS EDUCATIVAS ============
  const [tengoInstitucion, setTengoInstitucion] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) { setTengoInstitucion(false); return; }
    meService.getMisInstituciones()
      .then((list) => setTengoInstitucion(Array.isArray(list) && list.length > 0))
      .catch(() => setTengoInstitucion(false));
  }, [isAuthenticated]);
  // ============ FIN ALIANZAS EDUCATIVAS ============

  // ── Mensajes no leídos (real, chatService) ──────────────────────
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    if (!isAuthenticated) { setUnreadMessages(0); return; }
    chatService.getConversaciones()
      .then((list) => setUnreadMessages(list.reduce((sum, c) => sum + (c.unread || 0), 0)))
      .catch(() => setUnreadMessages(0));
  }, [isAuthenticated]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = location.pathname === '/';
  const transparentHeader = isHome && !scrolled;
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = user?.role === 'admin' || isFenagroSuperAdmin();
  const isInstructor = user?.role === 'instructor';
  useEffect(() => {
    if (!isAuthenticated) return;
    sesionOnlineService.getAll({ status: 'EnVivo', limit: 1 })
      .then((r) => setLiveSessions(r.total || 0))
      .catch(() => setLiveSessions(0));
  }, [isAuthenticated]);
  const liveTalks = liveSessions; // número de sesiones en vivo

  const navLinks = [
    { label: 'Inicio', path: '/', icon: <HomeIcon /> },
    { label: 'Cursos', path: '/courses', icon: <CoursesIcon /> },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setAnchorEl(null);
  };

  const getDashboardPath = () => {
    if (isAdmin) return '/admin';
    if (isInstructor) return '/instructor';
    return '/dashboard';
  };

  const getDashboardLabel = () => {
    if (isAdmin) return 'Panel Admin';
    if (isInstructor) return 'Panel Instructor';
    return 'Dashboard';
  };

  const getDashboardIcon = () => {
    if (isAdmin) return <AdminIcon />;
    if (isInstructor) return <SchoolIcon />;
    return <DashboardIcon />;
  };

  const getRoleColor = () => {
    if (isAdmin) return '#9c27b0';
    if (isInstructor) return '#2e7d32';
    return theme.palette.primary.main;
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={transparentHeader ? 0 : 4}
        sx={{
          background: transparentHeader
            ? 'transparent'
            : (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(10, 15, 26, 0.95)'
                  : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: transparentHeader ? 'none' : 'blur(20px)',
          borderBottom: transparentHeader
            ? 'none'
            : (theme) => `1px solid ${theme.palette.divider}`,
          color: transparentHeader ? '#fff' : (theme) => theme.palette.text.primary,
          boxShadow: transparentHeader ? 'none' : undefined,
          transition: 'background 0.3s ease, backdrop-filter 0.3s ease, border-bottom 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2, px: { xs: 2, md: 3 }, minHeight: { xs: 64, md: 72 } }}>
          {/* Logo */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', color: 'inherit' }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 2,
                background: `linear-gradient(135deg, ${getRoleColor()} 0%, ${theme.palette.primary.light} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {isAdmin ? <AdminIcon sx={{ color: 'white' }} /> : 
               isInstructor ? <SchoolIcon sx={{ color: 'white' }} /> :
               <SchoolIcon sx={{ color: 'white' }} />}
            </Box>
            {!isMobile && (
              <Typography variant="h6" fontWeight={700} sx={{ color: transparentHeader ? '#fff' : 'inherit' }}>
                {productoInfo.etiquetaLogo ? (
                  <>
                    {productoInfo.etiquetaLogo.prefijo}
                    <span style={{ color: transparentHeader ? '#f0d070' : getRoleColor() }}>
                      {productoInfo.etiquetaLogo.raiz}
                    </span>
                  </>
                ) : (
                  <span style={{ color: transparentHeader ? '#f0d070' : getRoleColor() }}>
                    {productoInfo.nombre}
                  </span>
                )}
              </Typography>
            )}
          </Box>

          {/* Navigation - Desktop */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 3 }}>
              {navLinks.map((link) => (
                <Button
                  key={link.path}
                  component={Link}
                  to={link.path}
                  sx={{
                    color: transparentHeader
                      ? '#fff'
                      : location.pathname === link.path ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname === link.path ? 600 : 400,
                    opacity: transparentHeader && location.pathname !== link.path ? 0.85 : 1,
                  }}
                >
                  {link.label}
                </Button>
              ))}
              
              {/* ============ Enlace Sesiones ============ */}
              {isAuthenticated && (
                <Button
                  component={Link}
                  to="/sesiones"
                  startIcon={
                    <Badge badgeContent={liveTalks} color="error">
                      <VideoCallIcon />
                    </Badge>
                  }
                  sx={{
                    color: transparentHeader
                      ? '#fff'
                      : location.pathname.includes('sesiones') ? 'primary.main' : 'text.primary',
                    fontWeight: location.pathname.includes('sesiones') ? 600 : 400,
                    opacity: transparentHeader && !location.pathname.includes('sesiones') ? 0.85 : 1,
                  }}
                >
                  Sesiones
                  {liveTalks > 0 && (
                    <Chip
                      size="small"
                      icon={<FiberManualRecord sx={{ fontSize: '8px !important', animation: 'pulse 1.5s infinite' }} />}
                      label="EN VIVO"
                      color="error"
                      sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Button>
              )}
              {/* ============ FIN NUEVO ============ */}
            </Box>
          )}

          {/* Search */}
          {!isMobile && (
            <Box
              component="form"
              onSubmit={handleSearch}
              sx={{
                flex: 1, maxWidth: 400,
                bgcolor: transparentHeader
                  ? 'rgba(255,255,255,0.15)'
                  : (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                borderRadius: 2, display: 'flex', alignItems: 'center', px: 2,
              }}
            >
              <SearchIcon sx={{ color: transparentHeader ? 'rgba(255,255,255,0.8)' : 'text.disabled', mr: 1 }} />
              <InputBase
                placeholder="Buscar cursos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: 1, py: 1,
                  color: transparentHeader ? '#fff' : 'inherit',
                  '& ::placeholder': { color: transparentHeader ? 'rgba(255,255,255,0.7)' : undefined, opacity: 1 },
                }}
              />
            </Box>
          )}

          {/* Right Side */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Theme Switch */}
            {!isMobile && (
              <Box sx={{
                display: 'flex', alignItems: 'center', mr: 1,
                bgcolor: transparentHeader ? 'rgba(255,255,255,0.15)' : 'action.hover',
                borderRadius: 2, px: 1, py: 0.5,
              }}>
                <LightModeIcon sx={{ fontSize: 18, color: transparentHeader ? '#fff' : 'inherit' }} />
                <Switch size="small" checked={isDarkMode} onChange={toggleTheme} sx={{ mx: 0.5 }} />
                <DarkModeIcon sx={{ fontSize: 18, color: transparentHeader ? '#fff' : 'inherit' }} />
              </Box>
            )}

            {/* ============ NUEVO: INDICADOR EN VIVO ============ */}
            {isAuthenticated && liveTalks > 0 && !isMobile && (
              <Tooltip title={`${liveTalks} sesión(es) en vivo`}>
                <IconButton
                  onClick={() => navigate('/sesiones')}
                  sx={{ 
                    color: 'error.main',
                    animation: 'pulse 2s infinite',
                  }}
                >
                  <FiberManualRecord />
                </IconButton>
              </Tooltip>
            )}
            {/* ============ FIN NUEVO ============ */}

            {isAuthenticated ? (
              <>
                {/* Chat Button */}
                {!isMobile && (
                  <IconButton component={Link} to={isInstructor ? '/instructor/chat' : '/chat'} sx={{ color: transparentHeader ? '#fff' : 'text.primary' }}>
                    <Badge badgeContent={unreadMessages} color="error"><ChatIcon /></Badge>
                  </IconButton>
                )}

                {!isMobile && (
                  <Button component={Link} to={getDashboardPath()} startIcon={getDashboardIcon()} sx={{ color: transparentHeader ? '#fff' : 'text.primary' }}>
                    {getDashboardLabel()}
                  </Button>
                )}

                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <Badge badgeContent={unreadMessages} color="error" invisible={!isMobile || unreadMessages === 0}>
                    <Avatar src={user?.avatar} sx={{ width: 36, height: 36, border: `2px solid ${getRoleColor()}` }}>
                      {user?.name?.[0]}
                    </Avatar>
                  </Badge>
                </IconButton>
                
                {/* User Menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { width: 250, mt: 1 } }}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography fontWeight={600}>{user?.name}</Typography>
                      {isAdmin && <Chip label="Admin" size="small" color="secondary" sx={{ height: 20, fontSize: '0.65rem' }} />}
                      {isInstructor && <Chip label="Instructor" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                  </Box>
                  <Divider />

                  {/* ============ SELECTOR DE ÁMBITO ============
                       Permite al usuario (admin del tenant o miembro de
                       varias instituciones) cambiar qué ámbito de datos
                       está mirando: la red completa del tenant o una
                       institución específica. Afecta a lo que ve en el
                       panel admin y en otras páginas conectadas al
                       contexto de ámbito. */}
                  {opcionesAmbito.length > 1 && (
                    <>
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Ámbito activo</InputLabel>
                          <Select
                            value={ambitoActivoId || ''}
                            label="Ámbito activo"
                            onChange={(e) => cambiarAmbito(e.target.value)}
                          >
                            {opcionesAmbito.map((o) => (
                              <MenuItem key={o.id} value={o.id}>{o.nombre}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Divider />
                    </>
                  )}
                  {/* ============ FIN SELECTOR DE ÁMBITO ============ */}

                  {/* ============ ALIANZAS EDUCATIVAS ============ */}
                  {isFenagroSuperAdmin() && (
                    <MenuItem component={Link} to="/instituciones/inscripcion" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><AccountBalanceIcon fontSize="small" /></ListItemIcon>Registrar institución
                    </MenuItem>
                  )}
                  {tengoInstitucion && (
                    <MenuItem component={Link} to="/mi-institucion" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><SchoolIcon fontSize="small" /></ListItemIcon>Mi institución
                    </MenuItem>
                  )}
                  {(isFenagroSuperAdmin() || tengoInstitucion) && <Divider />}
                  {/* ============ FIN ALIANZAS EDUCATIVAS ============ */}

                  {/* ============ REDEPROG SUPERADMIN (Fase 6) ============ */}
                  {isSuperAdminRedeprog && isSuperAdminRedeprog() && ([
                    <MenuItem key="redeprog-orgs" component={Link} to="/redeprog/organizaciones" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><AccountBalanceIcon fontSize="small" /></ListItemIcon>Organizaciones
                    </MenuItem>,
                    <Divider key="redeprog-divider" />,
                  ])}
                  {/* ============ FIN REDEPROG SUPERADMIN ============ */}

                  {isInstructor ? ([
                    <MenuItem key="instructor-dash" component={Link} to="/instructor" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>Panel Instructor
                    </MenuItem>,
                    <MenuItem key="instructor-students" component={Link} to="/instructor/students" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>Mis Estudiantes
                    </MenuItem>,
                    <MenuItem key="instructor-grading" component={Link} to="/instructor/grading" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><GradeIcon fontSize="small" /></ListItemIcon>Calificaciones
                    </MenuItem>,
                    <MenuItem key="instructor-chat" component={Link} to="/instructor/chat" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><Badge badgeContent={unreadMessages} color="error"><ChatIcon fontSize="small" /></Badge></ListItemIcon>Mensajes
                    </MenuItem>,
                    <MenuItem key="instructor-forums" component={Link} to="/instructor/forums" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><ForumIcon fontSize="small" /></ListItemIcon>Foros
                    </MenuItem>,
                    <MenuItem key="instructor-sesiones" component={Link} to="/sesiones" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon>
                        <Badge badgeContent={liveTalks} color="error">
                          <VideoCallIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      Sesiones
                    </MenuItem>,
                  ]) : isAdmin ? ([
                    <MenuItem key="admin-panel" component={Link} to="/admin" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon>Panel Admin
                    </MenuItem>,
                    <MenuItem key="admin-students" component={Link} to="/admin" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>Estudiantes
                    </MenuItem>,
                    <MenuItem key="admin-courses" component={Link} to="/admin" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><CoursesIcon fontSize="small" /></ListItemIcon>Gestionar Cursos
                    </MenuItem>,
                    <MenuItem key="admin-accesos" component={Link} to="/admin/accesos" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><ShieldIcon fontSize="small" /></ListItemIcon>Auditoría de contenido
                    </MenuItem>,
                    <MenuItem key="admin-certificados" component={Link} to="/admin/certificados" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><WorkspacePremiumIcon fontSize="small" /></ListItemIcon>Certificados
                    </MenuItem>,
                    <MenuItem key="admin-sesiones" component={Link} to="/sesiones" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon>
                        <Badge badgeContent={liveTalks} color="error">
                          <VideoCallIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      Sesiones
                    </MenuItem>,
                  ]) : ([
                    <MenuItem key="student-dash" component={Link} to="/dashboard" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>Dashboard
                    </MenuItem>,
                    <MenuItem key="student-chat" component={Link} to="/chat" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><Badge badgeContent={unreadMessages} color="error"><ChatIcon fontSize="small" /></Badge></ListItemIcon>Mensajes
                    </MenuItem>,
                    <MenuItem key="student-forums" component={Link} to="/forums" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><ForumIcon fontSize="small" /></ListItemIcon>Foros
                    </MenuItem>,
                    <MenuItem key="student-sesiones" component={Link} to="/sesiones" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon>
                        <Badge badgeContent={liveTalks} color="error">
                          <VideoCallIcon fontSize="small" />
                        </Badge>
                      </ListItemIcon>
                      Sesiones
                    </MenuItem>,
                    <MenuItem key="student-certificados" component={Link} to="/mis-certificados" onClick={() => setAnchorEl(null)}>
                      <ListItemIcon><WorkspacePremiumIcon fontSize="small" /></ListItemIcon>Mis certificados
                    </MenuItem>,
                    <MenuItem key="student-profile" component={Link} to="/profile" onClick={() => setAnchorEl(null)}>
                        <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>Mi Perfil
                      </MenuItem>,
                  ])}
                  
                  <Divider />
                  <MenuItem onClick={toggleTheme}>
                    <ListItemIcon>{isDarkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}</ListItemIcon>
                    {isDarkMode ? 'Modo Oscuro' : 'Modo Claro'}
                  </MenuItem>
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>Cerrar sesión
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                {!isMobile && (
                  <Button component={Link} to="/login" sx={{ color: transparentHeader ? '#fff' : 'text.primary' }}>
                    Iniciar sesión
                  </Button>
                )}
                <Button
                  component={Link}
                  to="/instituciones/inscripcion"
                  variant="contained"
                  sx={transparentHeader ? { bgcolor: '#f9a825', color: '#1a1a1a', '&:hover': { bgcolor: '#f0d070' } } : undefined}
                >
                  {isMobile ? 'Únete' : 'Mi institución'}
                </Button>
              </>
            )}

            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: transparentHeader ? '#fff' : 'inherit' }}><MenuIcon /></IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: 280 } }}>
        <Box sx={{ p: 2 }}>
          {isAdmin && <Chip icon={<AdminIcon />} label="Administrador" color="secondary" sx={{ mb: 2, width: '100%' }} />}
          {isInstructor && <Chip icon={<SchoolIcon />} label="Instructor" color="success" sx={{ mb: 2, width: '100%' }} />}
          
          {/* ============ NUEVO: ALERTA EN VIVO MÓVIL ============ */}
          {isAuthenticated && liveTalks > 0 && (
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<FiberManualRecord sx={{ animation: 'pulse 1.5s infinite' }} />}
              onClick={() => { navigate('/sesiones'); setMobileOpen(false); }}
              sx={{ mb: 2 }}
            >
              {liveTalks} Sesión(es) EN VIVO
            </Button>
          )}
          {/* ============ FIN NUEVO ============ */}
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LightModeIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2">Tema</Typography>
            </Box>
            <Switch checked={isDarkMode} onChange={toggleTheme} />
          </Box>
          
          <List>
            {navLinks.map((link) => (
              <ListItem key={link.path} component={Link} to={link.path} onClick={() => setMobileOpen(false)}
                sx={{ borderRadius: 2, mb: 0.5, bgcolor: location.pathname === link.path ? 'primary.main' : 'transparent',
                      color: location.pathname === link.path ? 'primary.contrastText' : 'text.primary' }}>
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{link.icon}</ListItemIcon>
                <ListItemText primary={link.label} />
              </ListItem>
            ))}
            
            {/* ============ Sesiones en móvil ============ */}
            {isAuthenticated && (
              <ListItem 
                component={Link} 
                to="/sesiones" 
                onClick={() => setMobileOpen(false)}
                sx={{ 
                  borderRadius: 2, 
                  mb: 0.5, 
                  bgcolor: location.pathname.includes('sesiones') ? 'primary.main' : 'transparent',
                  color: location.pathname.includes('sesiones') ? 'primary.contrastText' : 'text.primary' 
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  <Badge badgeContent={liveTalks} color="error">
                    <VideoCallIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary="Sesiones" />
              </ListItem>
            )}
            {/* ============ FIN NUEVO ============ */}
            
            {isAuthenticated && (
              <>
                <Divider sx={{ my: 1 }} />
                {isInstructor ? (
                  <>
                    <ListItem component={Link} to="/instructor" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><DashboardIcon /></ListItemIcon>
                      <ListItemText primary="Panel Instructor" />
                    </ListItem>
                    <ListItem component={Link} to="/instructor/grading" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><GradeIcon /></ListItemIcon>
                      <ListItemText primary="Calificaciones" />
                    </ListItem>
                    <ListItem component={Link} to="/instructor/chat" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><Badge badgeContent={unreadMessages} color="error"><ChatIcon /></Badge></ListItemIcon>
                      <ListItemText primary="Mensajes" />
                    </ListItem>
                    <ListItem component={Link} to="/instructor/forums" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><ForumIcon /></ListItemIcon>
                      <ListItemText primary="Foros" />
                    </ListItem>
                  </>
                ) : isAdmin ? (
                  <ListItem component={Link} to="/admin" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}><AdminIcon /></ListItemIcon>
                    <ListItemText primary="Panel Admin" />
                  </ListItem>
                ) : (
                  <>
                    <ListItem component={Link} to="/dashboard" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><DashboardIcon /></ListItemIcon>
                      <ListItemText primary="Dashboard" />
                    </ListItem>
                    <ListItem component={Link} to="/chat" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><Badge badgeContent={unreadMessages} color="error"><ChatIcon /></Badge></ListItemIcon>
                      <ListItemText primary="Mensajes" />
                    </ListItem>
                    <ListItem component={Link} to="/forums" onClick={() => setMobileOpen(false)} sx={{ borderRadius: 2 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}><ForumIcon /></ListItemIcon>
                      <ListItemText primary="Foros" />
                    </ListItem>
                  </>
                )}
              </>
            )}
          </List>
        </Box>
      </Drawer>

      {!isHome && <Toolbar />}

      {/* Botón volver arriba */}
      <Zoom in={scrolled}>
        <Fab
          color="primary"
          size="medium"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Volver arriba"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
        >
          <KeyboardArrowUp />
        </Fab>
      </Zoom>

      {/* ============ NUEVO: ESTILOS DE ANIMACIÓN ============ */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      {/* ============ FIN NUEVO ============ */}
    </>
  );
}

export default Header;


